import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Navigate, Outlet, redirect, useLocation, useRouter } from "@tanstack/react-router";
import { appSessionOptions, useAppSession } from "@/features/auth/session";
import { AccessError, AccessPending } from "@/features/auth/components/AccessStatus";

function SessionLayout() {
  const location = useLocation();
  const router = useRouter();
  const cache = useQueryClient();
  const routeActor = Route.useRouteContext().session.user.id;
  // beforeLoad populated this query. Observe documented Query revalidation without
  // repeating the initial check or maintaining a second auth store.
  const session = useAppSession({ refetchOnMount: false, refetchInterval: 60_000 });
  const currentActor = session.data?.user.id;
  useEffect(() => {
    if (!currentActor || currentActor === routeActor) return;
    let active = true;
    void (async () => {
      const predicate = (query: { queryKey: readonly unknown[] }) => query.queryKey[1] === routeActor;
      await cache.cancelQueries({ predicate });
      cache.removeQueries({ predicate });
      if (active) await router.invalidate();
    })();
    return () => { active = false; };
  }, [cache, router, currentActor, routeActor]);
  // Cached access remains visible while focus/polling checks run in the background.
  // Completed failures and expired sessions still close the private boundary below.
  if (session.isPending) return <AccessPending />;
  if (session.error) return <AccessError error={session.error} />;
  if (!session.data?.user.id) return <Navigate to="/auth/sign-in" search={{ next: location.href }} replace />;
  if (currentActor !== routeActor) return <AccessPending />;
  return <Outlet key={session.data.user.id} />;
}

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(appSessionOptions());
    if (!session?.user.id) throw redirect({ to: "/auth/sign-in", search: { next: location.href }, replace: true });
    return { session };
  },
  component: SessionLayout,
  pendingComponent: AccessPending,
  errorComponent: AccessError,
});
